/**
 * Page title row: "Reconcile" heading + subhead, "Upload statement"
 * button (Steel-tier gated — gradient-pill upsell when locked), and the
 * right-hand ExportMenu.
 *
 * Pure view. Caller wires the click → either opens the auto-reconcile
 * dialog or the upsell modal.
 */
import React from 'react';
import { Upload } from 'lucide-react';
import { Button } from '../../components/ui/button';
import ExportMenu from '../../components/ExportMenu';
import PageTitle from '../../components/PageTitle';
import { TAG_LABELS } from '../../lib/reconcileTags';
import { toUIType } from '../../lib/liabilityType';
import { MONTHS } from './constants';

const ReconcileHeader = ({
  isAutoReconcileUnlocked, onUploadClick,
  year, month, rows, accountNameMap, accountKindMap,
}) => (
  <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8 gap-4">
    <PageTitle
      title="Reconcile"
      subtitle="Tick off real-world payments. Estimates ask you to confirm the actual amount."
      testId="reconciliation-title"
      className="mb-0"
    />
    <div className="flex items-center gap-2">
      <Button
        type="button"
        onClick={onUploadClick}
        className={
          isAutoReconcileUnlocked
            ? 'bg-white border border-[#E2E8F0] text-[#020617] hover:bg-[#F8FAFC]'
            : 'text-white border-0 hover:opacity-90'
        }
        style={isAutoReconcileUnlocked ? undefined : { backgroundImage: 'linear-gradient(135deg, #64748B 0%, #0F172A 100%)', backgroundColor: 'transparent' }}
        data-testid="auto-reconcile-trigger"
      >
        <Upload className="w-4 h-4 mr-2" />
        Upload statement
        {!isAutoReconcileUnlocked && (
          <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-white/15 leading-none">Steel</span>
        )}
      </Button>
      <ExportMenu
        testIdPrefix="reconcile-export"
        getData={() => ({
          fileName: `forge-ledger-reconcile-${year}-${String(month).padStart(2, '0')}`,
          sheetName: `${MONTHS[month - 1].label} ${year}`.slice(0, 31),
          columns: [
            { header: 'Date', key: 'date', width: 12 },
            { header: 'Description', key: 'description', width: 32 },
            { header: 'Tag', key: 'tag', width: 12 },
            { header: 'Account', key: 'account', width: 22 },
            { header: 'Type', key: 'type', width: 12 },
            { header: 'Amount (GBP)', key: 'amount', width: 14 },
            { header: 'Category', key: 'category', width: 16 },
            { header: 'Estimated', key: 'estimated', width: 10 },
          ],
          rows: rows.map((r) => ({
            date: r.date,
            description: r.description,
            tag: TAG_LABELS[r.tag],
            account: accountNameMap[r.account_id] || '',
            type: r.transfer_id ? 'transfer' : toUIType(r.transaction_type, accountKindMap[r.account_id]),
            amount: r.amount,
            category: r.category || '',
            estimated: r.is_estimated ? 'Yes' : '',
          })),
        })}
      />
    </div>
  </div>
);

export default ReconcileHeader;
